/*
 * Site-wide PostHog loader (pageviews, pageleave, autocapture, session replay).
 *
 * layout/theme.liquid outputs window.NaomiPostHog = { key, host, store } and loads this
 * file only when settings.naomi_posthog_key is set and the page is not in the theme
 * editor. store is 'kr' on the KRW store, 'global' otherwise, and is registered as a
 * super property in the loaded callback, which posthog-js runs before the initial
 * $pageview, so every event carries it.
 *
 * Consent: loads Shopify's Customer Privacy API (the theme does not load it otherwise),
 * then posthog-js, only once Shopify.customerPrivacy.userCanBeTracked() is true. If the
 * API fails to load there is no consent signal, so nothing is ever sent. Re-checks on
 * visitorConsentCollected so a visitor who accepts mid-session is tracked without a
 * reload, and opts out if it is withdrawn. Never loads on customer account pages
 * (layout gate on template.directory): replay would record names and addresses, and
 * the URLs carry reset and activation tokens. Sets window.NaomiPostHog.ready once
 * posthog is initialised (and fires naomi:posthog-ready; NaomiPostHog.whenReady(fn) waits for it);
 * custom event senders (assets/naomi-pdp-track.js) drop events until then, never queue them.
 *
 * Checkout stitching: the checkout Web Pixel runs in a sandbox with its own anonymous
 * PostHog id, so once loaded this writes the ids onto the cart as hidden attributes
 * (_ph_did = distinct id, _ph_sid = session id; the pixel reads and bootstraps with
 * them). Writes only when the cart has items and the ids differ, on load and on every
 * cartUpdate, and never publishes cartUpdate itself. config.cart starts as the cart
 * Liquid rendered and then tracks what was written; if it rendered empty, load reads
 * /cart.js once to catch an add made before posthog loaded.
 *
 * checkout_clicked: the Checkout button in the cart drawer or on the cart page. The checkout
 * pixel can lose its events (in-app browsers), so this records intent from the storefront.
 * Props come from the data-cart-* attributes on that surface's cart ledger, which re-renders
 * on every cart change; value is cart.total_price in presentment currency (cents / 100, as
 * add_to_cart). Sent with sendBeacon from a passive listener, so the navigation never waits.
 */
(() => {
  const config = window.NaomiPostHog;
  if (!config || !config.key || window.Shopify?.designMode) return;

  let started = false;
  let stitching = false;
  const cart = config.cart || (config.cart = {});
  // fn once posthog is initialised (now, if it already is); never, without consent
  config.whenReady = (fn) => (config.ready ? fn() : document.addEventListener('naomi:posthog-ready', fn, { once: true }));

  // The attributes to write, or null when the cart is empty or already carries these ids.
  const stitchAttributes = (cart, did, sid) =>
    cart.item_count > 0 && did && (cart._ph_did !== did || cart._ph_sid !== sid) ? { _ph_did: did, _ph_sid: sid } : null;
  config.stitchAttributes = stitchAttributes; // exposed for tests/naomi-posthog-stitch.test.mjs

  // The surface's ledger dataset -> checkout_clicked props.
  const checkoutClickedProps = (surface, data, currency) => ({
    surface,
    item_count: Number(data.cartCount) || 0,
    value: (Number(data.cartTotal) || 0) / 100,
    currency,
  });
  config.checkoutClickedProps = checkoutClickedProps; // exposed for tests/naomi-posthog-stitch.test.mjs

  const stitchCart = () => {
    try {
      const ph = window.posthog;
      if (ph.has_opted_out_capturing()) return;
      const attributes = stitchAttributes(cart, ph.get_distinct_id(), ph.get_session_id());
      if (!attributes) return;
      Object.assign(cart, attributes); // optimistic, so overlapping updates write once
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      })
        .then((r) => {
          if (!r.ok) throw r;
        })
        .catch(() => {
          cart._ph_did = cart._ph_sid = null; // retry on the next cartUpdate
        });
    } catch {}
  };

  // cart.js and quick-add publish the whole cart; product-form publishes the added line
  // (no item_count, no attributes), which still means the cart has items.
  const onCartUpdate = (event) => {
    const data = event?.cartData;
    if (!data || data.status) return;
    if (typeof data.item_count === 'number') {
      cart.item_count = data.item_count;
      cart._ph_did = data.attributes?._ph_did;
      cart._ph_sid = data.attributes?._ph_sid;
    } else {
      cart.item_count = Math.max(cart.item_count || 0, 1);
    }
    stitchCart();
  };

  // array.js lives on the region's assets host: us.i.posthog.com -> us-assets.i.posthog.com,
  // eu.i.posthog.com -> eu-assets.i.posthog.com; a reverse proxy serves /static itself.
  const assetsHost = () => {
    const host = (config.host || '').replace(/\/+$/, '');
    if (!host) return 'https://us-assets.i.posthog.com';
    const m = /^(https?:\/\/)(us|eu)\.i\.posthog\.com$/.exec(host);
    return m ? `${m[1]}${m[2]}-assets.i.posthog.com` : host;
  };

  const loadPostHog = () => {
    const script = document.createElement('script');
    script.src = `${assetsHost()}/static/array.js`;
    script.async = true;
    script.onload = () => {
      if (!window.posthog || typeof window.posthog.init !== 'function') return;
      // Consent can be withdrawn while array.js downloads; there is no instance to opt out
      // yet, so re-check here and let a later consent start over.
      if (!canBeTracked()) {
        started = false;
        return;
      }
      window.posthog.init(config.key, {
        api_host: config.host,
        defaults: '2026-01-30',
        // The preset's history_change mode counted variant replaceState calls as pageviews; real loads only.
        capture_pageview: true,
        // Send each event at once: fast ad bounces and closing in-app browsers lost the batched ones.
        request_batching: false,
        loaded: (posthog) => {
          // Only reached with consent, so clear an opt-out left by an earlier withdrawal.
          if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
          posthog.register({ store: config.store });
          // Fallback join with the checkout pixel, which sends event.clientId (this cookie).
          const shopifyY = /(?:^|;\s*)_shopify_y=([^;]+)/.exec(document.cookie);
          if (shopifyY) posthog.register({ shopify_client_id: decodeURIComponent(shopifyY[1]) });
          config.ready = true;
          document.dispatchEvent(new Event('naomi:posthog-ready'));
          if (!stitching && typeof subscribe === 'function') {
            stitching = true;
            subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
          }
          stitchCart();
          // An add before posthog loaded published cartUpdate with no listener yet, so a rendered
          // empty cart may be stale: read the live cart once.
          if (!(cart.item_count > 0)) {
            fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
              .then((r) => r.json())
              .then((live) => {
                // Skip if empty, or if a cartUpdate already took over while this was in flight.
                if (!(live.item_count > 0) || cart.item_count > 0) return;
                cart.item_count = live.item_count;
                cart._ph_did = live.attributes?._ph_did;
                cart._ph_sid = live.attributes?._ph_sid;
                stitchCart();
              })
              .catch(() => {});
          }
        },
      });
    };
    document.head.appendChild(script);
  };

  const canBeTracked = () => {
    try {
      return !!window.Shopify?.customerPrivacy?.userCanBeTracked?.();
    } catch {
      return false;
    }
  };

  const checkConsent = () => {
    const allowed = canBeTracked();
    // Consent withdrawn mid-session: stop sending (posthog persists the opt-out).
    if (started && !allowed) window.posthog?.opt_out_capturing?.();
    if (started && allowed && window.posthog?.has_opted_out_capturing?.()) window.posthog.opt_in_capturing();
    if (started || !allowed) return;
    started = true;
    loadPostHog();
  };

  const withPrivacyApi = (cb) => {
    if (window.Shopify?.customerPrivacy) return cb();
    if (typeof window.Shopify?.loadFeatures !== 'function') return; // no API, no consent signal: stay off
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) return; // the API failed to load: no consent signal, stay off
      cb();
    });
  };

  withPrivacyApi(checkConsent);
  document.addEventListener('visitorConsentCollected', checkConsent);

  // The BOGOS gift app holds the first Checkout click and re-dispatches it, so one press arrives
  // as two clicks. ponytail: a 2 s window, not per page, so a bfcache return can count again.
  let lastCheckoutClick = 0;
  document.addEventListener(
    'click',
    (event) => {
      try {
        if (!config.ready) return;
        const button = event.target.closest?.('button[name="checkout"]');
        const area = button && (button.closest('cart-drawer') || button.closest('#main-cart-footer'));
        const ledger = area?.querySelector('[data-cart-total]');
        if (!ledger || Date.now() - lastCheckoutClick < 2000) return;
        lastCheckoutClick = Date.now();
        const surface = area.tagName === 'CART-DRAWER' ? 'drawer' : 'cart_page';
        const props = checkoutClickedProps(surface, ledger.dataset, window.Shopify?.currency?.active);
        window.posthog.capture('checkout_clicked', props, { transport: 'sendBeacon' });
      } catch {}
    },
    { capture: true, passive: true }
  );
})();
